/**
 * SWF Collection Hero — Booking-style filters
 * Dates: shareable links (?date=YYYY-MM-DD) + “All”; venue/brand stay in URL via history API
 * Venue/Brand: popover selectors with checkboxes (multi-select)
 * Logic: OR within group, AND across groups.
 */

class SwfCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.state = { date: new Set(), venue: new Set(), brand: new Set(), type: new Set() };

    const sectionRoot = this.closest('.swf-hero-section');
    const dataEl =
      (sectionRoot && sectionRoot.querySelector('[data-swf-product-filter-data]')) ||
      document.querySelector('[data-swf-product-filter-data]') ||
      document.getElementById('swf-product-filter-data');
    this.products = dataEl ? JSON.parse(dataEl.textContent) : [];

    // Popover triggers
    this.querySelectorAll('.swf-booking__trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePopover(trigger.dataset.opens);
      });
    });

    // Checkboxes in popovers
    this.querySelectorAll('.swf-booking__checkbox').forEach(cb => {
      cb.addEventListener('change', () => {
        const type = cb.dataset.filter;
        if (cb.checked) {
          this.state[type].add(cb.dataset.value);
        } else {
          this.state[type].delete(cb.dataset.value);
        }
        this.updateTriggerLabel(type);
        this.applyFilters();
      });
    });

    // Clear buttons inside popovers
    this.querySelectorAll('.swf-booking__popover-clear').forEach(btn => {
      btn.addEventListener('click', () => this.clearGroup(btn.dataset.clear));
    });

    // Featured type-filter buttons (e.g. "25+ brands, 100+ whiskies in one room" → type=expo)
    this.querySelectorAll('[data-type-filter]').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.typeFilter;
        const already = this.state.type.has(val);
        this.state.type.clear();
        if (!already) this.state.type.add(val);
        this.updateFeatureButtons();
        this.closeAllPopovers();
        this.applyFilters();
      });
    });

    // Clear all
    this.querySelectorAll('.swf-booking__clear-all, [data-clear-all]').forEach((btn) => {
      btn.addEventListener('click', () => this.clearAll());
    });

    // Date pill buttons
    this.querySelectorAll('[data-date-nav]').forEach(btn => {
      btn.addEventListener('click', () => {
        const nav = btn.dataset.dateNav;
        if (nav === 'all') {
          this.state.date.clear();
        } else {
          // Toggle: if already the only active date, clear; otherwise set to this date
          if (this.state.date.size === 1 && this.state.date.has(nav)) {
            this.state.date.clear();
          } else {
            this.state.date.clear();
            this.state.date.add(nav);
          }
        }
        this.applyFilters();
      });
    });

    // Hide date pills for days that have already passed — past days move to the Past events tab.
    this.hidePastDateNavs();

    // Close popovers on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.swf-booking__field')) {
        this.closeAllPopovers();
      }
    });

    // Summary refs
    this.summary = this.querySelector('.swf-booking__summary');
    this.summaryText = this.querySelector('.swf-booking__summary-text');

    // Restore filters from URL on load
    this.restoreFromURL();

    // Handle browser back/forward
    window.addEventListener('popstate', () => this.restoreFromURL());
  }

  /** Hide date-nav pills whose date is before today (Shopify viewer timezone). */
  hidePastDateNavs() {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    this.querySelectorAll('[data-date-nav]').forEach(btn => {
      const nav = btn.dataset.dateNav;
      if (!nav || nav === 'all') return;
      if (nav < todayKey) btn.hidden = true;
    });
  }

  /** Mark All / Fri / Sat / Sun pills to match ?date= (and multi-date fallbacks). */
  highlightDateNav() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('date');
    const parts = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
    const primary = parts.length === 0 ? null : parts[0];
    const allActive = parts.length === 0;

    this.querySelectorAll('[data-date-nav]').forEach(el => {
      const nav = el.dataset.dateNav;
      const isAll = nav === 'all';
      const on = isAll ? allActive : primary === nav;
      el.classList.toggle('swf-booking__date-link--active', on);
      if (on) el.setAttribute('aria-current', 'page');
      else el.removeAttribute('aria-current');
    });
  }

  togglePopover(name) {
    const field = this.querySelector(`[data-popover="${name}"]`);
    const popover = this.querySelector(`[data-popover-id="${name}"]`);
    if (!field || !popover) return;

    const isOpen = !popover.hidden;

    // Close all first
    this.closeAllPopovers();

    if (!isOpen) {
      popover.hidden = false;
      field.classList.add('swf-booking__field--open');
    }
  }

  closeAllPopovers() {
    this.querySelectorAll('.swf-booking__popover').forEach(p => p.hidden = true);
    this.querySelectorAll('.swf-booking__field').forEach(f => f.classList.remove('swf-booking__field--open'));
  }

  clearGroup(type) {
    this.state[type].clear();
    const group = this.querySelector(`[data-popover-id="${type}"]`);
    if (group) {
      group.querySelectorAll('.swf-booking__checkbox').forEach(cb => cb.checked = false);
    }
    this.updateTriggerLabel(type);
    this.applyFilters();
  }

  clearAll() {
    this.state = { date: new Set(), venue: new Set(), brand: new Set(), type: new Set() };
    this.querySelectorAll('.swf-booking__checkbox').forEach(cb => cb.checked = false);
    this.updateTriggerLabel('venue');
    this.updateTriggerLabel('brand');
    this.updateFeatureButtons();
    this.clearGridTab();
    this.applyFilters();
  }

  updateTriggerLabel(type) {
    const trigger = this.querySelector(`[data-opens="${type}"]`);
    if (!trigger) return;
    const valueEl = trigger.querySelector('.swf-booking__trigger-value');
    if (!valueEl) return;

    const count = this.state[type].size;
    if (count === 0) {
      valueEl.textContent = valueEl.dataset.default;
      valueEl.classList.remove('swf-booking__trigger-value--active');
    } else if (count === 1) {
      // Show the selected name
      const val = [...this.state[type]][0];
      const checkbox = this.querySelector(`.swf-booking__checkbox[data-filter="${type}"][data-value="${val}"]`);
      const label = checkbox ? checkbox.closest('.swf-booking__option').querySelector('.swf-booking__option-text').textContent : val;
      valueEl.textContent = label;
      valueEl.classList.add('swf-booking__trigger-value--active');
    } else {
      valueEl.textContent = `${count} selected`;
      valueEl.classList.add('swf-booking__trigger-value--active');
    }
  }

  updateFeatureButtons() {
    this.querySelectorAll('[data-type-filter]').forEach(btn => {
      const active = this.state.type.has(btn.dataset.typeFilter);
      btn.classList.toggle('swf-booking__feature--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  clearGridTab() {
    const wrap = document.querySelector('.collection-wrapper');
    if (wrap && wrap.dataset.swfGridTab) delete wrap.dataset.swfGridTab;
    document.querySelectorAll('[data-swf-grid-tab-control]').forEach((b) => {
      const active = !b.dataset.swfGridTabControl;
      b.classList.toggle('swf-sort__btn--active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  applyFilters() {
    const gridTab = document.querySelector('.collection-wrapper')?.dataset?.swfGridTab || '';
    const pubStopsTab = gridTab === 'pub-stops';
    const pastEventsTab = gridTab === 'past-events';
    const typeTabs = {
      tastings: new Set(['tasting', 'cocktail']),
      cigars: new Set(['cigar']),
      expo: new Set(['expo']),
      dnd: new Set(['dnd']),
    };
    const typeTab = typeTabs[gridTab];
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    // Past-dated events live exclusively on the Past events tab. On every other tab, hide them by default.
    // Items without a date (merch) are unaffected.
    const hidePastEvents = !pastEventsTab;

    const hasFilters =
      this.state.date.size > 0 ||
      this.state.venue.size > 0 ||
      this.state.brand.size > 0 ||
      this.state.type.size > 0 ||
      Boolean(typeTab) ||
      pubStopsTab ||
      pastEventsTab ||
      hidePastEvents;

    const visibleHandles = new Set();
    this.products.forEach(p => {
      let show = true;
      if (this.state.date.size > 0 && !this.state.date.has((p.date || '').substring(0, 10))) show = false;
      if (this.state.venue.size > 0 && !this.state.venue.has(p.venue)) show = false;
      if (this.state.brand.size > 0 && !this.state.brand.has(p.brand)) show = false;
      if (this.state.type.size > 0 && !this.state.type.has(p.type)) show = false;
      if (typeTab && !typeTab.has(p.type)) show = false;
      if (pubStopsTab) {
        if (p.type !== 'pub-stop') {
          show = false;
        } else {
          const v = String(p.venue || '').toLowerCase();
          if (v && !v.includes('snug')) show = false;
        }
      }
      if (pastEventsTab) {
        const dateKey = (p.date || '').substring(0, 10);
        if (!dateKey || dateKey >= todayKey) show = false;
      }
      if (hidePastEvents) {
        const dateKey = (p.date || '').substring(0, 10);
        if (dateKey && dateKey < todayKey) show = false;
      }
      if (show) visibleHandles.add(p.handle);
    });

    const gridItems = document.querySelectorAll('.product-grid__item, .product-grid > li');
    let visibleCount = 0;

    gridItems.forEach(item => {
      if (item.hasAttribute('data-swf-expo-callout')) return;
      const handle = this.getHandleFromItem(item);
      if (!hasFilters || visibleHandles.has(handle)) {
        item.removeAttribute('data-swf-hidden');
        visibleCount++;
      } else {
        item.setAttribute('data-swf-hidden', 'true');
      }
    });

    // Summary — short pill: "9 events". Filter detail is implicit
    // (the active date/venue/brand chips already show what's filtered).
    if (this.summary) {
      if (hasFilters) {
        this.summary.hidden = false;
        this.summaryText.textContent = `${visibleCount} event${visibleCount !== 1 ? 's' : ''}`;
      } else {
        this.summary.hidden = true;
      }
    }

    this.updateNoResults(hasFilters && visibleCount === 0);

    this.updateExpoCallout();

    this.syncToURL();
    this.highlightDateNav();
    this.broadcastFilterState();
  }

  getSingleSelectionLabel(type) {
    if (this.state[type].size !== 1) return null;
    const val = [...this.state[type]][0];
    const checkbox = this.querySelector(`.swf-booking__checkbox[data-filter="${type}"][data-value="${val}"]`);
    if (!checkbox) return val;
    const textEl = checkbox.closest('.swf-booking__option')?.querySelector('.swf-booking__option-text');
    return textEl ? textEl.textContent.trim() : val;
  }

  broadcastFilterState() {
    const venueCount = this.state.venue.size;
    const brandCount = this.state.brand.size;
    const typeActive = this.state.type.has('expo');
    document.dispatchEvent(new CustomEvent('swf:filters-changed', {
      detail: {
        venueCount,
        brandCount,
        venueLabel: this.getSingleSelectionLabel('venue'),
        brandLabel: this.getSingleSelectionLabel('brand'),
        expoActive: typeActive,
        dateKey: [...this.state.date].sort().join(',') || '',
      },
    }));
  }

  /**
   * Show an "also at the Brand Expo" callout card inside the product grid when the
   * filter makes it relevant: any brand is selected, or Fri 2026-05-15 / Sat 2026-05-16
   * is in the date filter (the two Expo nights). Links to /pages/expo.
   */
  updateExpoCallout() {
    const EXPO_DATES = new Set(['2026-05-15', '2026-05-16']);
    const brandActive = this.state.brand.size > 0;
    const dateMatchesExpo = [...this.state.date].some(d => EXPO_DATES.has(d));
    const show = brandActive || dateMatchesExpo;

    const grid = document.querySelector('.product-grid');
    if (!grid) return;

    let callout = grid.querySelector('[data-swf-expo-callout]');
    if (show && !callout) {
      callout = document.createElement('li');
      callout.className = 'product-grid__item swf-expo-callout-item';
      callout.setAttribute('data-swf-expo-callout', '');
      callout.innerHTML = `
        <a href="/pages/expo" class="swf-expo-callout">
          <img class="swf-expo-callout__brand-badge" data-swf-expo-brand-badge alt="" hidden>

          <span class="swf-expo-callout__eyebrow">
            <span class="swf-expo-callout__eyebrow-dot" aria-hidden="true"></span>
            Also at the festival
          </span>

          <span class="swf-expo-callout__mark" aria-hidden="true">
            <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 8h24l-3 22a6 6 0 0 1-6 5h-6a6 6 0 0 1-6-5L12 8Z"/>
              <path d="M13 16h22"/>
              <path d="M19 40h10"/>
              <path d="M24 40v4"/>
            </svg>
          </span>

          <span class="swf-expo-callout__kicker" data-swf-expo-kicker>Brand Expo</span>
          <span class="swf-expo-callout__title" data-swf-expo-title>Dunville's will be pouring</span>
          <span class="swf-expo-callout__body">Over 100 Irish whiskies to sample in one room — meet the distillers, taste rare pours, all under one roof.</span>

          <span class="swf-expo-callout__meta">
            <span class="swf-expo-callout__meta-pill">Fri &amp; Sat nights</span>
            <span class="swf-expo-callout__meta-sep" aria-hidden="true">·</span>
            <span>May 15 &amp; 16, 2026</span>
          </span>

          <span class="swf-expo-callout__cta">
            See Expo tickets
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m13 5 7 7-7 7"/></svg>
          </span>
        </a>
      `;
      grid.appendChild(callout);
    }

    if (callout) {
      callout.hidden = !show;
      if (show) {
        const title = callout.querySelector('[data-swf-expo-title]');
        const badge = callout.querySelector('[data-swf-expo-brand-badge]');
        if (title) {
          if (brandActive) {
            const brands = [...this.state.brand].map(handle => {
              const cb = this.querySelector(`.swf-booking__checkbox[data-filter="brand"][data-value="${handle}"]`);
              const text = cb && cb.closest('.swf-booking__option');
              const name = text ? text.querySelector('.swf-booking__option-text').textContent.trim() : handle;
              const logo = cb ? cb.getAttribute('data-brand-logo') || '' : '';
              return { name, logo };
            });
            const names = brands.map(b => b.name);
            const joined = names.length > 2
              ? `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
              : names.join(' & ');
            title.textContent = `${joined} will be pouring`;

            const logoBrand = brands.length === 1 ? brands[0] : null;
            if (badge) {
              if (logoBrand && logoBrand.logo) {
                badge.src = logoBrand.logo;
                badge.alt = logoBrand.name;
                badge.hidden = false;
              } else {
                badge.removeAttribute('src');
                badge.hidden = true;
              }
            }
          } else {
            title.textContent = 'Meet 25+ brands pouring 100+ whiskies';
            if (badge) {
              badge.removeAttribute('src');
              badge.hidden = true;
            }
          }
        }
      }
    }
  }

  getHandleFromItem(item) {
    if (item.dataset.handle) return item.dataset.handle;
    const link = item.querySelector('a[href*="/products/"]');
    if (link) {
      const match = link.href.match(/\/products\/([^?#/]+)/);
      if (match) return match[1];
    }
    const card = item.querySelector('product-card');
    if (card) {
      const cardLink = card.querySelector('a[href*="/products/"]');
      if (cardLink) {
        const match = cardLink.href.match(/\/products\/([^?#/]+)/);
        if (match) return match[1];
      }
    }
    return '';
  }

  syncToURL() {
    if (this._restoringFromURL) return;
    const params = new URLSearchParams();
    for (const [key, values] of Object.entries(this.state)) {
      if (values.size > 0) {
        params.set(key, [...values].join(','));
      }
    }
    const qs = params.toString();
    const url = window.location.pathname + (qs ? '?' + qs : '');
    window.history.pushState(null, '', url);
  }

  restoreFromURL() {
    const params = new URLSearchParams(window.location.search);

    // Reset state
    this.state = { date: new Set(), venue: new Set(), brand: new Set(), type: new Set() };
    this.querySelectorAll('.swf-booking__checkbox').forEach(cb => cb.checked = false);

    for (const key of ['date', 'venue', 'brand', 'type']) {
      const raw = params.get(key);
      if (!raw) continue;
      const values = raw.split(',').filter(Boolean);
      for (const val of values) {
        this.state[key].add(val);

        if (key === 'venue' || key === 'brand') {
          const cb = this.querySelector(`.swf-booking__checkbox[data-filter="${key}"][data-value="${val}"]`);
          if (cb) cb.checked = true;
        }
      }
      if (key === 'venue' || key === 'brand') this.updateTriggerLabel(key);
    }
    this.updateFeatureButtons();

    this.clearGridTab();

    this._restoringFromURL = true;
    this.applyFilters();
    this._restoringFromURL = false;
  }

  updateNoResults(show) {
    let el = document.querySelector('.swf-filter-no-results');
    const grid = document.querySelector('.product-grid, .product-grid-container');
    if (show) {
      if (!el && grid) {
        el = document.createElement('div');
        el.className = 'swf-filter-no-results';
        el.innerHTML = '<p>No events match your filters.</p><button type="button" class="swf-booking__clear-all" style="margin-top:0.5rem">Clear All Filters</button>';
        el.querySelector('button').addEventListener('click', () => this.clearAll());
        grid.parentNode.insertBefore(el, grid.nextSibling);
      }
      if (el) el.hidden = false;
    } else {
      if (el) el.hidden = true;
    }
  }
}

try {
  if (!customElements.get('swf-collection-filters')) {
    customElements.define('swf-collection-filters', SwfCollectionFilters);
  }
} catch (e) {
  console.warn('[SWF Collection Hero] swf-collection-filters', e);
}

/**
 * Dock the filter bar under the theme header when the sentinel passes that line.
 * Uses scroll + layout vars (top: 0 hid the bar under #header-group on collection pages).
 */
function initSwfCollectionHeroStickyNav() {
  const section = document.querySelector('.swf-hero-section');
  if (!section || section.dataset.swfStickyBound === 'true') return;

  const sentinel = section.querySelector('.swf-hero__sticky-sentinel');
  const panel = section.querySelector('.swf-hero__sticky-panel');
  const placeholder = section.querySelector('.swf-hero__sticky-placeholder');
  if (!sentinel || !panel || !placeholder) return;

  section.dataset.swfStickyBound = 'true';

  const sectionShopify = section.closest('.shopify-section');
  const main = document.querySelector('main#MainContent');
  const firstMainSection = main?.querySelector(':scope > .shopify-section');
  /** Venue Map page: filters-only hero is first in main — keep bar docked under header like post-scroll */
  const pinFiltersAlwaysFiltersOnly =
    section.classList.contains('swf-hero-section--filters-only') &&
    Boolean(sectionShopify && firstMainSection === sectionShopify);
  /** Mobile: always run with the compact docked bar — the masthead is
   *  CSS-hidden on mobile, so there's nothing to scroll past. Evaluated
   *  per tick so resize between mobile/desktop flips behavior live. */
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const pinFiltersAlways = () => pinFiltersAlwaysFiltersOnly || MOBILE.matches;

  let isFixed = false;

  /* Measure the visible bottom of the sticky header at viewport top.
     #header-group can include an announcement section that scrolls away,
     so --header-group-height (a fixed sum of all children) overshoots once
     the announcement is out of view. The visible bottom of header-component
     is the source of truth while it's pinned. */
  const readDockTopPx = () => {
    const headerEl = document.querySelector('header-component, .header-section');
    if (headerEl) {
      const rect = headerEl.getBoundingClientRect();
      if (rect.height > 0) return Math.max(0, rect.bottom);
    }
    const headerGroup = document.getElementById('header-group');
    if (headerGroup) {
      const rect = headerGroup.getBoundingClientRect();
      if (rect.height > 0) return Math.max(0, rect.bottom);
    }
    const mobileBar = document.querySelector('.swf-mm__bar');
    if (mobileBar) {
      const rect = mobileBar.getBoundingClientRect();
      if (rect.height > 0) return Math.max(0, rect.bottom);
    }
    const body = document.body;
    const root = document.documentElement;
    const sticky = parseFloat(getComputedStyle(root).getPropertyValue('--sticky-header-offset')) || 0;
    if (sticky > 0) return sticky;
    const headerVar = parseFloat(getComputedStyle(body).getPropertyValue('--header-height')) || 0;
    if (headerVar > 0) return headerVar;
    return 0;
  };

  const applyPlaceholderHeight = () => {
    if (!isFixed) return;
    placeholder.style.height = `${Math.round(panel.getBoundingClientRect().height)}px`;
  };

  const setFixed = (on) => {
    if (on === isFixed) return;
    if (on) {
      const h = Math.max(1, Math.round(panel.getBoundingClientRect().height));
      panel.classList.add('swf-hero__sticky-panel--fixed');
      placeholder.style.height = `${h}px`;
      isFixed = true;
      section.classList.add('swf-hero-section--filters-docked');
      requestAnimationFrame(applyPlaceholderHeight);
    } else {
      panel.classList.remove('swf-hero__sticky-panel--fixed');
      panel.style.removeProperty('top');
      placeholder.style.height = '0px';
      isFixed = false;
      section.classList.remove('swf-hero-section--filters-docked');
    }
  };

  /* Track the live dock position so the panel hugs the visible header bottom
     even as scrollable header siblings (announcements) move in and out. */
  const applyDockTop = (dock) => {
    if (!isFixed) return;
    /* −1px closes a hairline rendering gap; matches the legacy CSS rule. */
    panel.style.top = `${Math.max(0, Math.round(dock) - 1)}px`;
  };

  const tick = () => {
    if (document.body.classList.contains('swf-collection-swipe-mode')) {
      setFixed(false);
      section.classList.remove('swf-hero-section--filters-docked-release');
      return;
    }
    const dock = readDockTopPx();
    const top = sentinel.getBoundingClientRect().top;
    /* Match CSS top: dock − 1px */
    const shouldFix = pinFiltersAlways() || top <= dock - 0.5;

    /* Release the docked bar once the main content ends (so the footer
       is never partially hidden behind it). */
    const mainEl = document.querySelector('main#MainContent, main');
    let shouldRelease = false;
    if (mainEl) {
      const panelH = panel.getBoundingClientRect().height || 0;
      const mainBottom = mainEl.getBoundingClientRect().bottom;
      shouldRelease = mainBottom <= dock + panelH;
    }
    section.classList.toggle('swf-hero-section--filters-docked-release', shouldFix && shouldRelease);

    setFixed(shouldFix);
    if (shouldFix) applyDockTop(dock);
  };

  window.addEventListener('scroll', tick, { passive: true });
  window.addEventListener('resize', () => {
    tick();
    applyPlaceholderHeight();
  });
  window.addEventListener('load', tick, { once: true });
  MOBILE.addEventListener?.('change', () => {
    tick();
    applyPlaceholderHeight();
  });

  tick();
  requestAnimationFrame(tick);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSwfCollectionHeroStickyNav, { once: true });
} else {
  initSwfCollectionHeroStickyNav();
}
